<script lang="ts">
    import EditFormPage from '@Alternatives/Forms/Pages/EditFormPage'
    export default EditFormPage
</script>

<template>
    <div>
        <FormEditor :viewModel="formEditor" 
                    @save-form="handleRenameFormCommand" />

        <AlternativeEditorDialog :viewModel="alternativeEditorDialog" />

        <AttributeEditorDialog :viewModel="attributeEditorDialog"
                               @attribute-created="handleLoadAttribute" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div v-if="rm">
            <div id="form-section-editor" class="modal-mask" v-if="formSectionEditor.opened">
                <div class="modal-wrapper">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">{{formSectionEditor.title}}</h5>
                                <button @click="formSectionEditor.close()" type="button" class="close">&times;</button>
                            </div>
                            <div class="modal-body">
                                <form id="form-designer-form-section-editor-form" @submit.prevent="handleFormSectionEditorSubmit">
                                    <div class="form-group">
                                        <label>Name:</label>
                                        <input type="text" v-model.trim="formSectionEditor.name" class="form-control" required minlength="2" />
                                        <small class="text-danger">{{formSectionEditor.nameError}}</small>
                                    </div>
                                </form>
                            </div>
                            <div class="modal-footer">
                                <button @click="formSectionEditor.close()" type="button" class="btn btn-outline-secondary">Cancel</button>
                                <button type="submit" form="form-designer-form-section-editor-form" class="btn btn-primary">{{formSectionEditor.confimButtonLabel}}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
                <div class="row">
                    <div class="col-lg-6">
                        <h6>
                            <a class="text-dark" @click="handleBack">
                                <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back
                            </a>
                        </h6>
                        <h1 class="pointer" style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;" @click="showFormEditor">
                            {{formName}}
                        </h1>
                    </div>
                    <div class="col-lg-6 text-right">
                        <button type="button" class="btn btn-outline-secondary mr-1" style="padding: 0.7em 2em;" @click="showFormPreview">Preview</button>
                        
                        <button type="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="handleUpdateFormDefinitionCommand">
                            {{saving ? 'Saving...' : 'Save form'}}
                        </button>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0;" />

            <div class="container-fluid p-0">
                <div class="row no-gutters">
                    <div class="col-lg-4 col-md-5 col-sm-6" style="max-height: calc(100vh - 250px); height: calc(100vh - 250px); overflow-y: auto;">
                        <div v-if="selectedField == null" style="padding-left: 15px;">
                            <h6 class="mt-4">CREATE FORM ELEMENTS</h6>
                            <hr />
                            <button class="btn btn-outline-secondary mr-1" @click="showFormSectionCreator">Form Section</button>
                            <button class="btn btn-outline-secondary" @click="showAttributeCreator">Attribute</button>

                            <h6 class="mt-4">ADD FORM FIELDS</h6>
                            <hr />

                            <div id="FormFieldsManagerPlaceholder">
                                <div class="input-group">
                                    <input type="search" placeholder="Search..." class="form-control" v-model="searchPhrase">
                                    <span class="input-group-append">
                                        <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                    </span>
                                </div>
                                <div class="mt-3">
                                    <template v-for="s in attributesSections" :key="s.Name">
                                        <div class="mt-3" v-if="s.filteredAttributes.length">
                                            <h5 class="font-weight-light">
                                                <a class="text-dark" @click="s.Collapsed = !s.Collapsed">
                                                    <i class="fa mr-1" :class="{'fa-angle-down': !s.Collapsed, 'fa-angle-right': s.Collapsed}"></i>
                                                    <span v-if="s.Name.length > 0">{{ s.Name }}</span>
                                                    <span v-else class="font-italic">Uncategorized</span>
                                                </a>
                                            </h5>
                                            <div v-if="s.Collapsed === false">
                                                <div class="row no-gutters">
                                                    <div class="col-6 pb-3 pr-2" v-for="attr in s.filteredAttributes" :key="attr.Id">
                                                        <div v-if="attr.IsIncluded" class="border rounded px-2 py-3 bg-light">
                                                            <p class="mb-0 text-ellipsis font-weight-light text-muted">
                                                                <span class="name" :title="attr.AttributeName">
                                                                    {{ ellipsis(attr.AttributeName, 30) }}
                                                                </span>
                                                            </p>
                                                        </div>
                                                        <div v-else class="border rounded px-2 py-3 bg-warning"
                                                             :title="attr.AttributeName"
                                                             style="cursor: move;"
                                                             @dragstart="handleDragStart($event, attr)"
                                                             @dragend="handleDragEnd($event, attr)"
                                                             draggable="true">
                                                            <p class="mb-0 text-ellipsis font-weight-light">
                                                                <span class="name">
                                                                    {{ ellipsis(attr.AttributeName, 30) }}
                                                                </span>
                                                            </p>
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <div v-if="selectedField != null">
                            <div class="py-3 pl-3 bg-warning">
                                <h5 class="mb-0">Editing the form field</h5>
                            </div>

                            <div class="py-4" style="padding: 15px;">
                                <form id="field-editor-form" @submit.prevent="handleFieldEditorFormSubmit">
                                    <div class="form-group">
                                        <label>Attribute:</label>
                                        <input type="text" class="form-control" :value="selectedField.AttributeName" readonly />
                                    </div>

                                    <div class="form-group">
                                        <label>Label:</label>
                                        <input type="text" class="form-control" v-model.trim="selectedField.FieldName" required minlength="2" maxlength="100" />
                                    </div>

                                    <div class="form-group">
                                        <label>Description:</label>
                                        <textarea rows="3" class="form-control" v-model.trim="selectedField.FieldDescription" />
                                        <small class="text-muted">This is text that will be displayed below the form field. Like this one!</small>
                                    </div>

                                    <div class="form-group">
                                        <div class="form-check">
                                            <input class="form-check-input" type="checkbox" v-model="selectedField.IsFieldRequired" @change="handleSelectedFieldIsRequiredChange">
                                            <label class="form-check-label">Required field</label>
                                        </div>
                                    </div>

                                    <div class="form-group">
                                        <div class="form-check">
                                            <input class="form-check-input" type="checkbox" v-model="selectedField.IsFieldReadonly" @change="handleSelectedFieldIsReadonlyChange">
                                            <label class="form-check-label">Readonly field</label>
                                        </div>
                                    </div>

                                    <div class="text-center mt-5">
                                        <button type="submit" form="field-editor-form" class="btn btn-warning">
                                            Done field editing
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-8 col-md-7 col-sm-6 bg-light" style="padding: 20px 20px 20px 5px; max-height: calc(100vh - 250px); height: calc(100vh - 250px); overflow-y: auto;">
                        <div style="padding-left: 30px;">
                            <div class="form-group mt-3 px-2 py-1">
                                <label>Blurb:</label>
                                <textarea rows="1" class="form-control" v-model.trim="blurb" />
                                <small class="form-text text-muted">Here you can provide a short blurb that will be displayed above the form fields. Up to 100 characters.</small>
                                <div class="error">{{blurbError}}</div>
                            </div>
                            <div class="mt-3 px-2 py-1">
                                <p class="mb-1">
                                    <span class="text-danger">* </span>{{alternativeLabel}} Name
                                    (<a class="text-primary" @click="nameReadonly = !nameReadonly">{{nameReadonly ? 'unset readonly' : 'set readonly'}}</a>)
                                </p>
                                <div class="border rounded" :class="{'bg-color-gray3': nameReadonly, 'bg-white': !nameReadonly}">
                                    <p class="text-muted mx-3 my-2">Single line text...</p>
                                </div>
                            </div>
                            <div class="mt-3 px-2 py-1">
                                <p class="mb-1">
                                    Description
                                    <span v-if="includeDescription">
                                        (<a class="text-primary" @click="descriptionReadonly = !descriptionReadonly">{{descriptionReadonly ? 'unset readonly' : 'set readonly'}}</a>)
                                    </span>
                                </p>
                                <div class="clearfix">
                                    <label class="switch float-left" style="margin-top: 4px;">
                                        <input type="checkbox" v-model="includeDescription">
                                        <span class="slider round"> </span>
                                    </label>
                                    <div v-if="includeDescription" style="margin-left: 65px;" class="border rounded" :class="{'bg-color-gray3': descriptionReadonly, 'bg-white': !descriptionReadonly}">
                                        <p class="text-muted mx-3 my-2">Multi-line text..</p>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-3 px-2 py-1">
                                <p class="mb-1">
                                    Status
                                    <span v-if="includeStatus">
                                        (<a class="text-primary" @click="statusReadonly = !statusReadonly">{{statusReadonly ? 'unset readonly' : 'set readonly'}}</a>)
                                    </span>
                                </p>
                                <div class="clearfix">
                                    <label class="switch float-left" style="margin-top: 4px;">
                                        <input type="checkbox" v-model="includeStatus">
                                        <span class="slider round"> </span>
                                    </label>
                                    <div v-if="includeStatus" style="margin-left: 65px;" class="border rounded" :class="{'bg-color-gray3': statusReadonly, 'bg-white': !statusReadonly}">
                                        <p class="text-muted mx-3 my-2">Drop-down select...</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-for="s in formSectionsView" :key="s.Name">
                            <div v-if="s.Name" style="margin-left: 25px;">
                                <h5><span class="editable editable-warning" @click="showFormSectionEditor(s)">{{s.Name}}</span></h5>
                            </div>
                            <div class="drop-area"
                                 @dragover.prevent
                                 @dragenter.prevent="s.IsHovering = true"
                                 @dragleave="s.IsHovering = false"
                                 @drop.prevent="handleDropFormSection($event, s)"
                                 :class="{'drop-area-hover': s.IsHovering, 'drop-area-show': draggingAttributeId != null}">
                            </div>

                            <div class="alert alert-warning mb-4 mx-4" role="alert" v-if="isFormSectionEmptyWarningVisible(s)">
                                Empty sections are not supported. Please add at least 1 field or
                                <a class="text-primary" @click="handleRemoveSection(s)">remove this section</a>.
                                If you close this window without taking one of these actions, your empty section will be deleted.
                            </div>

                            <div v-for="f in s.Fields" :key="f.Id">
                                <div class="d-flex form-field-container" :class="{'selected': selectedAttributeId === f.Id, 'hoverable': isFormFieldHoverable(f)}">
                                    <div class="drag-handler bg-warning d-flex align-items-center justify-content-center"
                                         :title="f.FieldName"
                                         style="cursor: move;"
                                         draggable="true"
                                         @dragstart="handleDragStart($event, f)"
                                         @dragend="handleDragEnd($event, f)">
                                        <i class="fa fa-ellipsis-v text-white" aria-hidden="true"></i>
                                        <span class="name d-none">{{f.FieldName}}</span>
                                    </div>
                                    <div class="w-100 form-field" @click.stop="selectedAttributeId = f.Id" title="Edit field">
                                        <a class="float-right text-dark" @click.stop="handleRemoveField(f)" title="Remove field">
                                            <i class="fa fa-trash" aria-hidden="true"></i>
                                        </a>
                                        <p class="mb-1">
                                            <span class="text-danger" v-if="f.IsFieldRequired">*</span>
                                            <span class="name">{{f.FieldName}}</span>
                                            <i class="fa fa-pencil text-muted"></i>
                                        </p>
                                        <div class="border rounded" :class="{'bg-color-gray3': f.IsFieldReadonly, 'bg-white': !f.IsFieldReadonly}">
                                            <p class="text-muted mx-3 my-2">&nbsp;</p>
                                        </div>
                                        <small class="text-muted">{{f.FieldDescription}}</small>
                                    </div>
                                </div>


                                <div class="drop-area"
                                     @dragover.prevent
                                     @dragenter.prevent="f.IsHovering = true"
                                     @dragleave="f.IsHovering = false"
                                     @drop.prevent="handleDropFormField($event, s, f)"
                                     :class="{'drop-area-hover': f.IsHovering, 'drop-area-show': draggingAttributeId != null }">
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<style scoped>
    .no-style {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        color: inherit;
        cursor: pointer;
    }

    .form-field {
        padding: 3px 7px;
    }

    .form-field-container .fa-trash:hover {
        color: #d9534f;
    }

    .hoverable:hover .fa-pencil, .hoverable:hover .fa-trash {
        visibility: visible;
    }

    .form-field-container .fa-pencil, .form-field-container .fa-trash {
        visibility: hidden;
    }

    .drop-area {
        height: 20px;
        margin-left: 32px;
        margin-right: 7px;
        transition: background-color 0.3s ease, border-color 0.3s ease;
        border: 1px solid transparent;
    }

    .drop-area-show {
        border: 1px dashed orange;
        background-color: #FED8B1;
    }
    /* When active (hovering with a draggable item) */
    .drop-area-hover {
        border-color: orange;
        background-color: orange !important;
    }

    .drag-handler {
        width: 25px;
        cursor: move;
        visibility: hidden;
    }


    .hoverable:hover .drag-handler {
        visibility: visible !important;
    }

    .hoverable:hover .form-field {
        border: 1px solid #f0ad4e;
        border-left: 0;
        padding: 2px 6px 2px 7px;
        cursor: pointer;
    }

    .selected .form-field {
        border: 1px solid #f0ad4e;
        border-left: 0;
        background-color: rgba(86, 86, 90, 0.10) !important;
        padding: 2px 6px 2px 7px;
    }

    .selected .drag-handler {
        visibility: visible !important;
    }

    .selected .fa-trash {
        visibility: visible;
    }
</style>


